import React from 'react';
import { Badge, Severity, Tooltip } from '@plerionhq/design-system';
import type { SeverityLevel } from '@plerionhq/design-system';
import TimeAgo from 'react-timeago';
import clsx from 'clsx';
import type { CellValue } from './types';
import type {
  ColumnElement,
  ColumnGroup,
  BadgeOptions,
  ImageOptions,
} from '../../visualization-schema';
import {
  convertImageOptions,
  formatFullDateTime,
  processTemplate,
  isUrl,
} from './utils';
import { ChatMarkdown } from '@plerionhq/design-system/unstable';
import { Image } from '../../../../../ui/Image';
import { LinkOutIcon } from '@plerionhq/design-system/experimental';
// Helper function to render image
export const renderImage = (value: CellValue, options?: ImageOptions) => {
  if (!value) {
    return null;
  }

  const imageProps = convertImageOptions(options);
  return (
    <div
      className={`h-10 w-10 flex-shrink-0 self-start overflow-hidden rounded-md`}
    >
      <Image
        {...imageProps}
        style={options?.style}
        name={String(value)}
        className="w-full flex-shrink-0 object-cover"
      />
    </div>
  );
};

// Helper function to render badge
export const renderBadge = (
  value: CellValue,
  options?: BadgeOptions,
  rowData?: Record<string, CellValue>,
) => {
  if (!value) {
    return null;
  }

  // Evaluate href template if it contains {{}} placeholders
  let href = options?.href;
  if (href && rowData && /\{\{/.test(href)) {
    href = processTemplate(href, rowData as Record<string, unknown>);
  }

  // Auto-detect URL from value if href is not provided and value looks like a URL
  if (!href && isUrl(value)) {
    href = String(value);
  }

  const label = href ? (
    <a
      className="hyperlink text-wrap break-all"
      href={href}
      rel={options?.isExternal ? 'noreferrer noopener' : undefined}
      target={options?.isExternal ? '_blank' : undefined}
    >
      {String(value)}
      {options?.isExternal && (
        <LinkOutIcon href={href} className="ml-1 inline-block h-4 w-4" />
      )}
    </a>
  ) : (
    String(value)
  );

  const badge = (
    <div
      className={clsx(
        'overflow-hidden',
        options?.maxWidth && `max-w-[${options.maxWidth}px]`,
      )}
      style={{
        ...(options?.maxWidth && {
          maxWidth: `${options.maxWidth}px`,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }),
      }}
    >
      <Badge
        label={label}
        status={options?.status}
        icon={
          options?.icon ? (
            <span
              className="h-4 w-4"
              dangerouslySetInnerHTML={{ __html: options.icon }}
            />
          ) : undefined
        }
        className="!flex !w-full !flex-col !items-start !gap-y-2 text-wrap break-words"
      />
    </div>
  );

  return options?.tooltip ? (
    <Tooltip label={options.tooltip}>
      <div>{badge}</div>
    </Tooltip>
  ) : (
    badge
  );
};

// Helper function to render cell based on column type
export const renderCell = (
  value: CellValue,
  columnType?: string,
  element?: ColumnElement,
  rowData?: Record<string, CellValue>,
) => {
  if (!value) {
    return null;
  }

  switch (columnType) {
    case 'severity': {
      return <Severity severityLevel={`${value}` as SeverityLevel} />;
    }
    case 'id': {
      return (
        <code className="rounded bg-gray-100 px-2 py-1 dark:bg-gray-800">
          {String(value)}
        </code>
      );
    }
    case 'date': {
      return new Date(String(value)).toLocaleString();
    }
    case 'datetime': {
      try {
        const date = new Date(String(value));
        return (
          <Tooltip label={formatFullDateTime(String(value))}>
            <span className="cursor-help whitespace-pre">
              <TimeAgo date={date} />
            </span>
          </Tooltip>
        );
      } catch (e) {
        return String(value);
      }
    }
    case 'image': {
      return renderImage(value, element?.imageOptions);
    }
    case 'badge': {
      return renderBadge(value, element?.badgeOptions as BadgeOptions, rowData);
    }
    case 'markdown': {
      return <ChatMarkdown content={String(value)} />;
    }
    default: {
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return String(value);
    }
  }
};

// Helper function to render a column element
const renderColumnElement = (
  element: ColumnElement,
  value: CellValue,
  rowData?: Record<string, CellValue>,
) => {
  const content = renderCell(value, element.columnType, element, rowData);

  const containerStyles = {
    ...(element.width ? { width: element.width } : {}),
    ...element.containerStyle,
  };

  if (!element.label && !element.className && !element.style) {
    return content;
  }

  return (
    <div className={element.containerClassName} style={containerStyles}>
      {element.label && (
        <span
          className={clsx(
            'mr-1 text-gray-500 dark:text-gray-400',
            element.labelClassName,
          )}
          style={element.labelStyle}
        >
          {element.label}:
        </span>
      )}
      <div className={element.className} style={element.style}>
        {content}
      </div>
    </div>
  );
};

// Helper function to render a column group
export const renderColumnGroup = (
  group: ColumnGroup,
  data: Record<string, CellValue>,
) => {
  const renderElements = (elements: (ColumnElement | ColumnGroup)[]) => {
    return elements.map((element, index) => {
      // If it's a nested group
      if ('elements' in element) {
        const gridStyles =
          element.layout === 'grid' && element.gridConfig
            ? {
                display: 'grid',
                gap: element.gridConfig.gap,
                rowGap: element.gridConfig.rowGap,
                columnGap: element.gridConfig.columnGap,
                gridTemplateColumns:
                  element.gridConfig.templateColumns ||
                  (element.gridConfig.columns
                    ? `repeat(${element.gridConfig.columns}, minmax(0, 1fr))`
                    : undefined),
                gridTemplateRows: element.gridConfig.templateRows,
                ...element.style,
              }
            : element.style;

        return (
          <div
            key={index}
            className={clsx(
              element.className,
              element.layout === 'row' && 'flex items-center',
              element.layout === 'column' && 'flex flex-col',
              element.spacing && `gap-${element.spacing}`,
            )}
            style={gridStyles}
          >
            {renderElements(element.elements)}
          </div>
        );
      }

      // If it's a leaf element
      const value = data[element.field];
      const elementNode = renderColumnElement(element, value, data);

      // If there's a separator and it's not the last element
      if (group.separator && index < elements.length - 1) {
        return (
          <React.Fragment key={index}>
            {elementNode}
            <span
              className={clsx('mx-2 text-gray-300', group.separatorClassName)}
              style={group.separatorStyle}
            >
              {group.separator}
            </span>
          </React.Fragment>
        );
      }

      return <React.Fragment key={index}>{elementNode}</React.Fragment>;
    });
  };

  const gridStyles =
    group.layout === 'grid' && group.gridConfig
      ? {
          display: 'grid',
          gap: group.gridConfig.gap,
          rowGap: group.gridConfig.rowGap,
          columnGap: group.gridConfig.columnGap,
          gridTemplateColumns:
            group.gridConfig.templateColumns ||
            (group.gridConfig.columns
              ? `repeat(${group.gridConfig.columns}, minmax(0, 1fr))`
              : undefined),
          gridTemplateRows: group.gridConfig.templateRows,
          ...group.style,
        }
      : group.style;

  return (
    <div
      className={clsx(
        group.className,
        group.layout === 'row' && 'flex items-center',
        group.layout === 'column' && 'flex flex-col',
        group.spacing && `gap-${group.spacing}`,
      )}
      style={gridStyles}
    >
      {renderElements(group.elements)}
    </div>
  );
};
